import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { socket } from "../socket";
import { acquireSocketConnection, releaseSocketConnection } from "../socketLifecycle";
import { RoleContext } from "../Contexts/RoleContext";
import {
  getThreads,
  getThreadMessages,
  getWhatsAppStatus,
  sendCounsellorMessage,
  updateThreadStatus,
} from "../Components/whatsapp/whatsappApi";
import { url as apiBaseUrl } from "../Components/url";
import s from "./WhatsAppInbox.module.scss";

const DEFAULT_BUSINESS_ID = Number(process.env.REACT_APP_DEFAULT_BUSINESS_ID || 1);

const META_WA_START =
  "https://developers.facebook.com/docs/whatsapp/cloud-api/get-started";

function WhatsAppLogoIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" inheritViewBox>
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </SvgIcon>
  );
}

function senderLabel(senderType) {
  if (senderType === "ai") return "AI Bot";
  if (senderType === "counsellor") return "Counsellor";
  return "Student";
}

function senderIcon(senderType) {
  if (senderType === "ai") {
    return <SmartToyOutlinedIcon sx={{ fontSize: 14, opacity: 0.85 }} />;
  }
  if (senderType === "counsellor") {
    return <SupportAgentIcon sx={{ fontSize: 14, opacity: 0.85 }} />;
  }
  return <PersonOutlineIcon sx={{ fontSize: 14, opacity: 0.85 }} />;
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (isToday) return "Today";

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return "Yesterday";

  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function avatarColor(phone) {
  const colors = [
    "#00a884", "#53bdeb", "#7f66ff", "#ff7eb3",
    "#ffa348", "#25d366", "#02735e", "#5b72e0",
  ];
  let hash = 0;
  for (let i = 0; i < (phone || "").length; i++) {
    hash = phone.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function phoneInitial(phone) {
  if (!phone) return "?";
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.slice(-2) || "?";
}

function statusBadgeClass(status) {
  if (status === "open") return s.statusBadgeOpen;
  if (status === "pending") return s.statusBadgePending;
  return s.statusBadgeClosed;
}

export default function WhatsAppInboxPage() {
  const queryClient = useQueryClient();
  const { role } = useContext(RoleContext);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [composer, setComposer] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);

  const normalizedRole = (role || "").trim().toLowerCase();
  const canRegisterAccount = normalizedRole === "admin" || normalizedRole === "manager";

  const statusQuery = useQuery({
    queryKey: ["whatsapp-status", DEFAULT_BUSINESS_ID],
    queryFn: () => getWhatsAppStatus(DEFAULT_BUSINESS_ID),
    staleTime: 60_000,
  });

  const inboxEnabled = useMemo(() => {
    if (statusQuery.isLoading) return false;
    if (statusQuery.isError) return true;
    return statusQuery.data?.connected === true;
  }, [statusQuery.isLoading, statusQuery.isError, statusQuery.data]);

  const showConnectPanel =
    !statusQuery.isLoading && !statusQuery.isError && statusQuery.data?.connected === false;

  const threadsQuery = useQuery({
    queryKey: ["whatsapp-threads", statusFilter],
    queryFn: () =>
      getThreads({
        businessId: DEFAULT_BUSINESS_ID,
        status: statusFilter || undefined,
        page: 1,
        perPage: 50,
      }),
    enabled: inboxEnabled,
    refetchInterval: inboxEnabled ? 15000 : false,
  });

  const activeThread = useMemo(
    () => (threadsQuery.data?.items || []).find((t) => t.id === activeThreadId) || null,
    [threadsQuery.data, activeThreadId]
  );

  const messagesQuery = useQuery({
    queryKey: ["whatsapp-messages", activeThreadId],
    queryFn: () => getThreadMessages(activeThreadId, { page: 1, perPage: 100 }),
    enabled: Boolean(activeThreadId) && inboxEnabled,
  });

  useEffect(() => {
    const items = threadsQuery.data?.items || [];
    if (!activeThreadId && items.length) {
      setActiveThreadId(items[0].id);
    }
  }, [threadsQuery.data, activeThreadId]);

  const messages = messagesQuery.data?.items || [];

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  useEffect(() => {
    if (!inboxEnabled) return;
    const ownerKey = "whatsapp-inbox";
    const joinBusinessRoom = () => {
      socket.emit("whatsapp:join_business", { business_id: DEFAULT_BUSINESS_ID });
    };

    acquireSocketConnection(ownerKey);
    if (socket.connected) joinBusinessRoom();
    else socket.on("connect", joinBusinessRoom);

    return () => {
      socket.off("connect", joinBusinessRoom);
      releaseSocketConnection(ownerKey);
    };
  }, [inboxEnabled]);

  useEffect(() => {
    if (!inboxEnabled || !activeThreadId) return;
    const joinThreadRoom = () => {
      socket.emit("whatsapp:join_thread", { thread_id: activeThreadId });
    };
    if (socket.connected) joinThreadRoom();
    else socket.on("connect", joinThreadRoom);

    return () => {
      socket.off("connect", joinThreadRoom);
      if (socket.connected) {
        socket.emit("whatsapp:leave_thread", { thread_id: activeThreadId });
      }
    };
  }, [inboxEnabled, activeThreadId]);

  useEffect(() => {
    if (!inboxEnabled) return;
    function onNewMessage(payload) {
      const thread = payload?.thread;
      if (!thread) return;
      queryClient.invalidateQueries({ queryKey: ["whatsapp-threads"] });
      if (thread.id === activeThreadId) {
        queryClient.invalidateQueries({ queryKey: ["whatsapp-messages", activeThreadId] });
      }
    }
    socket.on("whatsapp:new_message", onNewMessage);
    socket.on("whatsapp:thread_updated", onNewMessage);
    socket.on("whatsapp:thread_assignment_updated", onNewMessage);
    return () => {
      socket.off("whatsapp:new_message", onNewMessage);
      socket.off("whatsapp:thread_updated", onNewMessage);
      socket.off("whatsapp:thread_assignment_updated", onNewMessage);
    };
  }, [inboxEnabled, activeThreadId, queryClient]);

  const onSend = async () => {
    if (!activeThreadId || !composer.trim()) return;
    try {
      setSaving(true);
      await sendCounsellorMessage({
        thread_id: activeThreadId,
        content: composer.trim(),
        message_type: "text",
      });
      setComposer("");
      await queryClient.invalidateQueries({ queryKey: ["whatsapp-messages", activeThreadId] });
      await queryClient.invalidateQueries({ queryKey: ["whatsapp-threads"] });
    } finally {
      setSaving(false);
    }
  };

  const markResolved = async () => {
    if (!activeThreadId) return;
    await updateThreadStatus(activeThreadId, "closed");
    await queryClient.invalidateQueries({ queryKey: ["whatsapp-threads"] });
  };

  const threads = threadsQuery.data?.items || [];
  const webhookUrl = `${apiBaseUrl}/api/whatsapp/webhook`;

  if (statusQuery.isLoading) {
    return (
      <div className={s.page}>
        <div className={s.pageLoading}>
          <CircularProgress size={40} sx={{ color: "#128C7E" }} aria-label="Loading" />
        </div>
      </div>
    );
  }

  if (showConnectPanel) {
    return (
      <div className={s.page}>
        <div className={s.topBar}>
          <div className={s.topBarTitle}>
            <div className={s.waIcon}>
              <WhatsAppLogoIcon sx={{ fontSize: 20 }} />
            </div>
            WhatsApp Shared Inbox
          </div>
          <div className={s.topBarActions}>
            <span className={s.roleBadge}>{role || "staff"}</span>
          </div>
        </div>
        <div className={s.connectShell}>
          <div className={s.connectCard}>
            <div className={s.connectCardHeader}>
              <div className={s.connectCardIcon}>
                <WhatsAppLogoIcon sx={{ fontSize: 30 }} />
              </div>
              <div>
                <h1 className={s.connectTitle}>Connect WhatsApp Business</h1>
                <p className={s.connectLead}>
                  No WhatsApp Cloud API account is registered for business {DEFAULT_BUSINESS_ID}.
                  Connect Meta WhatsApp to receive messages in this inbox.
                </p>
              </div>
            </div>
            <ol className={s.connectSteps}>
              <li className={s.connectStep}>
                <span className={s.connectStepNum}>1</span>
                <div className={s.connectStepBody}>
                  <div className={s.connectStepTitle}>Create or open a Meta app</div>
                  <p className={s.connectStepText}>
                    In Meta for Developers, add the WhatsApp product to your app and note your
                    phone number ID and access token.
                  </p>
                </div>
              </li>
              <li className={s.connectStep}>
                <span className={s.connectStepNum}>2</span>
                <div className={s.connectStepBody}>
                  <div className={s.connectStepTitle}>Configure the webhook</div>
                  <p className={s.connectStepText}>
                    Set the callback URL and verify token in the WhatsApp configuration. Use this
                    callback URL:
                  </p>
                  <code className={s.connectWebhook}>{webhookUrl}</code>
                </div>
              </li>
              <li className={s.connectStep}>
                <span className={s.connectStepNum}>3</span>
                <div className={s.connectStepBody}>
                  <div className={s.connectStepTitle}>Register the account in EMS</div>
                  <p className={s.connectStepText}>
                    An administrator must save your phone number ID, token, and verify token to
                    the backend so inbound messages are accepted.
                  </p>
                </div>
              </li>
            </ol>
            <div className={s.connectActions}>
              <a
                className={s.connectBtnPrimary}
                href={META_WA_START}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open WhatsApp Cloud API guide
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </a>
              <button
                type="button"
                className={s.connectBtnSecondary}
                onClick={() => queryClient.invalidateQueries({ queryKey: ["whatsapp-status"] })}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
                Check connection again
              </button>
            </div>
            {canRegisterAccount ? (
              <div className={s.connectAdminNote}>
                <strong>Administrator:</strong> register the account with a POST to{" "}
                <code style={{ fontSize: "0.85em" }}>/api/whatsapp/accounts</code> (JWT required)
                including <code>business_id</code>, <code>phone_number_id</code>,{" "}
                <code>access_token</code>, and <code>webhook_verify_token</code>. Optional:{" "}
                <code>app_secret</code>, <code>display_phone_number</code>.
                <pre className={s.connectAdminCode}>
{`POST ${apiBaseUrl}/api/whatsapp/accounts
Content-Type: application/json

{
  "business_id": ${DEFAULT_BUSINESS_ID},
  "phone_number_id": "<from Meta>",
  "access_token": "<permanent or system user token>",
  "webhook_verify_token": "<same as in Meta webhook>",
  "app_secret": "<optional, for signature verification>",
  "display_phone_number": "+1..."
}`}
                </pre>
              </div>
            ) : (
              <p className={s.connectStaffNote}>
                You do not have permission to register the account. Ask an administrator or manager
                to complete the connection using the API or your deployment runbook.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.topBar}>
        <div className={s.topBarTitle}>
          <div className={s.waIcon}>
            <WhatsAppLogoIcon sx={{ fontSize: 20 }} />
          </div>
          WhatsApp Shared Inbox
        </div>
        <div className={s.topBarActions}>
          <span className={s.roleBadge}>{role || "staff"}</span>
        </div>
      </div>

      <div className={s.layout}>
        <div className={s.chatPanel}>
          {activeThread ? (
            <>
              <div className={s.chatHeader}>
                <div className={s.chatHeaderInfo}>
                  <div
                    className={s.chatHeaderAvatar}
                    style={{ background: avatarColor(activeThread.phone_number) }}
                  >
                    {phoneInitial(activeThread.phone_number)}
                  </div>
                  <div className={s.chatHeaderText}>
                    <div className={s.chatHeaderName}>{activeThread.phone_number}</div>
                    <div className={s.chatHeaderMeta}>
                      <span
                        className={`${s.statusBadge} ${statusBadgeClass(activeThread.status)}`}
                      >
                        {activeThread.status}
                      </span>
                      {activeThread.tag && <span className={s.tagBadge}>{activeThread.tag}</span>}
                      {activeThread.last_message_at && (
                        <span>Last active {formatDate(activeThread.last_message_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={s.chatHeaderActions}>
                  <button
                    className={s.resolveBtn}
                    disabled={!activeThread || activeThread.status === "closed"}
                    onClick={markResolved}
                  >
                    <CheckCircleOutlineIcon style={{ fontSize: 16, marginRight: 4, verticalAlign: -2 }} />
                    Resolve
                  </button>
                </div>
              </div>

              <div className={s.messagesArea}>
                {messagesQuery.isLoading && (
                  <div className={s.loadingIndicator}>
                    <CircularProgress size={22} sx={{ color: "#128C7E" }} />
                    <span style={{ marginLeft: 10 }}>Loading messages</span>
                  </div>
                )}

                {!messagesQuery.isLoading && messages.length === 0 && (
                  <div className={s.emptyMessages}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: "#94a3b8" }} aria-hidden />
                    <span>No messages yet. Send a reply below when the customer writes in.</span>
                  </div>
                )}

                {messages.map((message) => {
                  const isOutbound =
                    message.sender_type === "counsellor" || message.sender_type === "ai";
                  return (
                    <div
                      key={message.id}
                      className={`${s.msgRow} ${isOutbound ? s.outbound : ""}`}
                    >
                      <div
                        className={`${s.msgBubble} ${isOutbound ? s.msgOutbound : s.msgInbound}`}
                      >
                        <div className={s.msgSender}>
                          {senderIcon(message.sender_type)}
                          <span>{senderLabel(message.sender_type)}</span>
                        </div>
                        <div className={s.msgText}>
                          {message.content || (
                            <span className={s.noContent}>[No text content]</span>
                          )}
                        </div>
                        <div className={s.msgTime}>
                          {formatTime(message.created_at)}
                          {isOutbound && (
                            <DoneAllIcon style={{ fontSize: 14, color: "#53bdeb" }} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className={s.composer}>
                <input
                  type="text"
                  className={s.composerInput}
                  placeholder="Type a message"
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  disabled={saving}
                  aria-label="Message reply"
                />
                <button
                  type="button"
                  className={s.sendBtn}
                  onClick={onSend}
                  disabled={saving || !composer.trim()}
                  aria-label="Send message"
                >
                  <SendIcon style={{ fontSize: 20 }} />
                </button>
              </div>
            </>
          ) : (
            <div className={s.welcomeContainer}>
              <div className={s.welcomeIconLarge}>
                <WhatsAppLogoIcon sx={{ fontSize: 44 }} />
              </div>
              <h2 className={s.welcomeTitle}>WhatsApp Shared Inbox</h2>
              <p className={s.welcomeDesc}>
                Select a conversation from the panel on the right to view messages.
                Inbound messages from students appear on the left; your team and AI replies
                align on the right.
              </p>
            </div>
          )}
        </div>

        <aside className={s.sidebar}>
          <div className={s.sidebarHeader}>
            <div className={s.sidebarHeaderRow}>
              <div>
                <div className={s.sidebarTitle}>
                  <ForumOutlinedIcon sx={{ fontSize: 18, color: "#54656f" }} />
                  Conversations
                </div>
                <div className={s.sidebarSubtitle}>Business {DEFAULT_BUSINESS_ID}</div>
              </div>
              <button
                className={s.refreshBtn}
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ["whatsapp-threads"] });
                  queryClient.invalidateQueries({ queryKey: ["whatsapp-status"] });
                }}
                title="Refresh"
                type="button"
              >
                <RefreshIcon style={{ fontSize: 20 }} />
              </button>
            </div>
            <div className={s.searchBar}>
              <select
                className={s.filterSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className={s.threadList}>
            {threadsQuery.isLoading ? (
              <div className={s.emptyThreads}>
                <CircularProgress size={32} sx={{ color: "#128C7E" }} />
              </div>
            ) : threads.length === 0 ? (
              <div className={s.emptyThreads}>
                <div className={s.emptyThreadsIcon}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
                </div>
                <div className={s.emptyThreadsTitle}>No conversations yet</div>
                <div className={s.emptyThreadsDesc}>
                  When messages arrive from WhatsApp, they will appear here.
                </div>
              </div>
            ) : (
              threads.map((thread) => {
                const active = thread.id === activeThreadId;
                return (
                  <div
                    key={thread.id}
                    role="button"
                    tabIndex={0}
                    className={`${s.threadItem} ${active ? s.threadItemActive : ""}`}
                    onClick={() => setActiveThreadId(thread.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveThreadId(thread.id);
                      }
                    }}
                  >
                    <div
                      className={s.threadAvatar}
                      style={{ background: avatarColor(thread.phone_number) }}
                    >
                      {phoneInitial(thread.phone_number)}
                    </div>
                    <div className={s.threadInfo}>
                      <div className={s.threadTopRow}>
                        <span className={s.threadName}>{thread.phone_number}</span>
                        <span className={s.threadTime}>
                          {formatDate(thread.last_message_at)}
                        </span>
                      </div>
                      <div className={s.threadBottomRow}>
                        <span className={s.threadPreview}>
                          {thread.status === "closed" ? "Resolved" : "Tap to view"}
                        </span>
                        <span
                          className={`${s.statusBadge} ${statusBadgeClass(thread.status)}`}
                        >
                          {thread.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
